import * as React from 'react';
import { Field, InjectedFormProps } from 'redux-form';
import { IImageData, ICropperOptions, FieldArrayFieldsProps } from 'models/common';
import { ImageUpload } from 'components/FormControls';
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc';
import { IModalAction } from 'models/modal';
import { cropAndSaveFormImage } from 'redux/modules/image';
import { thumbUrl } from 'helpers/common';
import renderIcon from 'helpers/renderIcon';
const { connect } = require('react-redux');

interface IMeta {
	error: any;
	submitFailed: boolean;
	form: string;
}

interface IProps extends InjectedFormProps {
	images: IImageData[];
	fields: FieldArrayFieldsProps<any>;
	meta: IMeta;
	maxImageSize?: number;
	selectedImages?: string[];
	selectedThumbnail?: string;
	selectedImagesFieldName?: string;
	selectedThumbnailFieldName?: string;
	enableSelectThumbnail?: boolean;
	enableSelectImage?: boolean;
	label?: string;
	imageDescription?: string;
	uploadDescription?: string;
	uploadImageIcon?: JSX.Element;
	cropImage?: boolean;
	cropImageOptions?: ICropperOptions;
	cropImageModalTitle?: string;

	/* functions props */
	change: (form: string, field: string, data: any) => void;
	onDelete: (index: string, id: string) => void;
	imageUploadFailed?: (error: any) => void;
	imageUploadSuccess?: () => void;
	showModal?: (modalType: string, modalProps: any) => IModalAction;
	closeModal?: () => IModalAction;
	cropAndSaveFormImage?: (image: IImageData, fieldName: string, formName: string, images: any[]) => void;
}

const SortableItem = SortableElement(({ href, onDelete }) => {
	return (
		<div className="formImagesDragAndDropElement">
			<div className="backgroundImage" style={{ backgroundImage: `url(${thumbUrl(href)})` }} />
			<div className="buttons">
				<button className="iconBtn round" type="button" onClick={onDelete}>
					{renderIcon('delete_2')}
				</button>
			</div>
		</div>
	);
});

const ImagesSortable = SortableContainer(({ items, onDelete, imageUploadElement }) => {
	return (
		<div className="wrapperImagesUploaded">
			<div className="wrapperUploadElementsDrop">{imageUploadElement}</div>
			{items.map((item: IImageData, index) => (
				<SortableItem key={index} index={index} href={item.href} onDelete={() => onDelete(index, item.id)} />
			))}
			<div className="wrapperImageDragDrop"></div>
		</div>
	);
});

@connect(
	() => ({}),
	(dispatch) => ({
		cropAndSaveFormImage: (image: IImageData, fieldName: string, formName: string, images: any[]) =>
			dispatch(cropAndSaveFormImage(image, fieldName, formName, images)),
	}),
)
class FormImages extends React.Component<IProps, any> {
	constructor(props) {
		super(props);

		this.imageUploadedCallback = this.imageUploadedCallback.bind(this);
		this.imageUploadFailed = this.imageUploadFailed.bind(this);
		this.imageUploadSuccess = this.imageUploadSuccess.bind(this);
		this.onSortEnd = this.onSortEnd.bind(this);
		this.deleteImage = this.deleteImage.bind(this);
		this.onSelectImage = this.onSelectImage.bind(this);
		this.onSelectThumbnail = this.onSelectThumbnail.bind(this);
		this.deleteSelectedImages = this.deleteSelectedImages.bind(this);
		this.deselectAllImages = this.deselectAllImages.bind(this);
		this.deleteAllImages = this.deleteAllImages.bind(this);
		this.openCropImageModal = this.openCropImageModal.bind(this);
	}

	private onSortEnd = ({ oldIndex, newIndex }) => {
		const images: IImageData[] = this.props.fields.map((_, index) => {
			return this.props.fields.get(index);
		});

		const newOrderImages = arrayMove(images, oldIndex, newIndex);

		this.props.change(this.props.meta.form, this.props.fields.name, newOrderImages);

		this.forceUpdate();
	};

	public imageUploadedCallback(id: string, href: string) {
		this.props.fields.push({
			id,
			href,
			index: this.props.fields.length,
			description: null,
			author: null,
		});

		if (this.props.imageUploadSuccess) {
			this.props.imageUploadSuccess();
		}

		this.forceUpdate();
	}

	private imageUploadFailed(error: any) {
		if (this.props.imageUploadFailed) {
			this.props.imageUploadFailed(error);
		}
	}

	private imageUploadSuccess() {
		if (this.props.imageUploadSuccess) {
			this.props.imageUploadSuccess();
		}
	}

	private deleteImage(index: number, id: string) {
		this.props.fields.remove(index || 0);

		const selectedImages = this.props.selectedImages;
		if (selectedImages && selectedImages.length > 0) {
			const imageIndex = selectedImages.indexOf(id);
			if (imageIndex > -1) {
				selectedImages.splice(imageIndex, 1);
				this.props.change(this.props.meta.form, this.props.selectedImagesFieldName, selectedImages);
			}
		}

		let thumbnail = this.props.selectedThumbnail;
		if (thumbnail === id) {
			thumbnail = null;
			this.props.change(this.props.meta.form, this.props.selectedThumbnailFieldName, thumbnail);
		}
	}

	private onSelectImage(selected: boolean, id: string) {
		let selectedImages = this.props.selectedImages;
		if (selected) {
			if (!selectedImages) {
				selectedImages = [];
			}
			selectedImages.push(id);
		} else {
			const imageIndex = selectedImages.indexOf(id);
			selectedImages.splice(imageIndex, 1);
		}
		this.props.change(this.props.meta.form, this.props.selectedImagesFieldName, selectedImages);
		this.forceUpdate();
	}

	private onSelectThumbnail(selected: boolean, id: string) {
		let thumbnail = this.props.selectedThumbnail;
		if (selected) {
			thumbnail = id;
		} else {
			thumbnail = null;
		}
		this.props.change(this.props.meta.form, this.props.selectedThumbnailFieldName, thumbnail);
	}

	private deleteSelectedImages() {
		const images: IImageData[] = this.props.images;
		const selectedImages = this.props.selectedImages;

		selectedImages.forEach((imageId) => {
			const imageIndex = images.findIndex((x) => x.id === imageId);
			if (imageIndex > -1) {
				images.splice(imageIndex, 1);
			}
		});

		this.props.change(this.props.meta.form, this.props.fields.name, images);
		this.props.change(this.props.meta.form, this.props.selectedImagesFieldName, []);
	}

	private deselectAllImages() {
		this.props.change(this.props.meta.form, this.props.selectedImagesFieldName, []);
	}

	private deleteAllImages() {
		this.props.fields.removeAll();
		this.props.change(this.props.meta.form, this.props.selectedImagesFieldName, []);
		this.props.change(this.props.meta.form, this.props.selectedThumbnailFieldName, null);
	}

	private openCropImageModal() {
		let images: IImageData[] = [];
		if (this.props.fields && this.props.fields.length > 0) {
			images = this.props.fields.map((_, index) => {
				return this.props.fields.get(index);
			});
		}

		this.props.showModal('CropImageModal', {
			closeModal: this.props.closeModal,
			saveImage: (image: IImageData) => {
				this.props.cropAndSaveFormImage(image, this.props.fields.name, this.props.meta.form, images);
			},
			modalTitle: this.props.cropImageModalTitle,
			cropOptions: this.props.cropImageOptions,
		});
	}

	public render() {
		const { maxImageSize, fields, cropImage, cropImageOptions } = this.props;

		const uploadImageIcon = this.props.uploadImageIcon ? this.props.uploadImageIcon : renderIcon('upload');

		let images: IImageData[] = [];
		if (fields && fields.length > 0) {
			images = fields.map((_, index) => {
				return fields.get(index);
			});
		}

		const openCropImageModalElement = <div onClick={this.openCropImageModal}>{uploadImageIcon}</div>;

		return (
			<div className="imageUploadWrapper">
				<ImagesSortable
					items={images}
					onSortEnd={this.onSortEnd}
					onDelete={this.deleteImage}
					axis={'xy'}
					distance={5}
					imageUploadElement={
						cropImage && cropImageOptions ? (
							openCropImageModalElement
						) : (
							<Field
								name={'uploadImageField'}
								component={ImageUpload}
								imageUploadedCallback={this.imageUploadedCallback}
								imageUploadFailed={this.imageUploadFailed}
								imageUploadSuccess={this.imageUploadSuccess}
								maxImageSize={maxImageSize}
								uploadImageIcon={uploadImageIcon}
							/>
						)
					}
				/>
			</div>
		);
	}
}

export { FormImages };
